/**
 * Palantir PMS - Software para la gestion de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creacion / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso esta restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React from "react";
import { format } from "date-fns";
import { usePmsTheme } from "./PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { getCurrencyMinorUnits } from "../currency/currencyPrecision";

const DAY_COUNT = 14;

function pad2(num) {
  return String(num).padStart(2, "0");
}

function toIsoLocal(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseIsoLocal(raw) {
  const str = String(raw || "").slice(0, 10);
  const [y, m, d] = str.split("-").map((v) => Number(v));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const date = new Date(y, m - 1, d);
  if (!Number.isFinite(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

function normalizeStayMin(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

function normalizeStayMax(raw) {
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

export function formatStayLengthPrice(value, currencyCode, currencyCatalog = []) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  const code = String(currencyCode || "EUR").trim().toUpperCase() || "EUR";
  return `${amount.toFixed(getCurrencyMinorUnits(code, currencyCatalog))} ${code}`;
}

const StayLengthTable = React.memo(function StayLengthTable({
  priceHistory,
  stopSells = [],
  roomRateValidUntil,
  roomRateId,
  focusDate,
  clearFocusDate,
  referenceDate,
  currency,
}) {
  const { themeId } = usePmsTheme();
  const { baseCurrency, currencyCatalog } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const windowDates = React.useMemo(() => {
    const anchor = parseIsoLocal(focusDate) || parseIsoLocal(referenceDate) || new Date();
    anchor.setHours(0, 0, 0, 0);

    return Array.from({ length: DAY_COUNT }, (_, idx) => {
      const day = new Date(anchor);
      day.setDate(anchor.getDate() + idx);
      return {
        day,
        iso: toIsoLocal(day),
      };
    });
  }, [focusDate, referenceDate]);

  const windowSet = React.useMemo(() => new Set(windowDates.map((d) => d.iso)), [windowDates]);

  const rules = React.useMemo(() => {
    return (priceHistory || [])
      .map((rule) => {
        const startDate = parseIsoLocal(rule?.start_date);
        const endDate = parseIsoLocal(rule?.end_date);
        const price = Number.parseFloat(rule?.price);
        if (!startDate || !endDate || !Number.isFinite(price)) return null;

        return {
          startTs: startDate.getTime(),
          endTs: endDate.getTime(),
          price,
          minStay: normalizeStayMin(rule?.min_stay),
          maxStay: normalizeStayMax(rule?.max_stay),
        };
      })
      .filter(Boolean);
  }, [priceHistory]);

  const stopSellSet = React.useMemo(() => {
    const set = new Set();
    const targetRateId = Number(roomRateId);

    const pushIso = (iso) => {
      if (windowSet.has(iso)) set.add(iso);
    };

    (stopSells || []).forEach((ss) => {
      if (
        Number.isFinite(targetRateId) &&
        ss?.room_rate_id != null &&
        Number(ss.room_rate_id) !== targetRateId
      ) {
        return;
      }

      const stayDate = parseIsoLocal(ss?.stay_date);
      if (stayDate) {
        pushIso(toIsoLocal(stayDate));
        return;
      }

      const startDate = parseIsoLocal(ss?.start_date);
      const endDate = parseIsoLocal(ss?.end_date);
      if (!startDate || !endDate) return;

      for (
        let cursor = new Date(startDate);
        cursor <= endDate;
        cursor.setDate(cursor.getDate() + 1)
      ) {
        pushIso(toIsoLocal(cursor));
      }
    });

    return set;
  }, [roomRateId, stopSells, windowSet]);

  const rows = React.useMemo(() => {
    return windowDates.map((d) => {
      const ts = d.day.getTime();
      let selected = null;

      for (let i = 0; i < rules.length; i += 1) {
        const rule = rules[i];
        if (ts < rule.startTs || ts > rule.endTs) continue;
        if (!selected || rule.price < selected.price) {
          selected = rule;
        }
      }

      const blocked = stopSellSet.has(d.iso);
      return {
        iso: d.iso,
        displayDate: format(d.day, "dd/MM/yyyy"),
        price: selected ? selected.price : null,
        minStay: selected ? selected.minStay : null,
        maxStay: selected ? selected.maxStay : null,
        blocked,
      };
    });
  }, [rules, stopSellSet, windowDates]);

  // ── paletas de color ──────────────────────────────────────────────────────
  const L = isLightTheme;

  const rootStyle   = { color: L ? "#26354f" : "#dfe8f7" };
  const titleStyle  = { margin: 0, fontSize: "0.92rem", fontWeight: 800, color: L ? "#172653" : "#f2f6ff" };
  const subStyle    = { margin: "3px 0 0", fontSize: "0.75rem", color: L ? "#5b6680" : "#aebbd0" };
  const btnStyle    = {
    borderRadius: "999px",
    border: L ? "1px solid #7c90a7" : "1px solid rgba(61,182,233,0.62)",
    color: L ? "#223a7a" : "#dce8fa",
    background: L ? "#edf8fc" : "rgba(61,182,233,0.12)",
    fontSize: "0.75rem", fontWeight: 700, lineHeight: 1, padding: "6px 10px",
  };
  const wrapStyle   = {
    overflowX: "auto", borderRadius: "12px",
    border: L ? "1px solid #d7e2ec" : "1px solid rgba(92,134,181,0.34)",
    background: L ? "#ffffff" : "#121f3d",
  };
  const theadThStyle = {
    background: "#223a7a",
    color: "#ffffff",
    borderBottom: "2px solid #3db6e9",
    fontWeight: 700,
    padding: "7px 9px", textAlign: "center", whiteSpace: "nowrap",
  };
  const cellBorder  = L ? "1px solid #e2eaf1" : "1px solid rgba(92,134,181,0.22)";
  const tdBase      = { padding: "7px 9px", textAlign: "center", whiteSpace: "nowrap", borderRight: cellBorder, borderBottom: cellBorder };
  const tdLast      = { ...tdBase, borderRight: 0 };

  const dateColor   = L ? "#26354f" : "#dfe8f7";
  const priceColor  = L ? "#223a7a" : "#d8f3f0";
  const mutedColor  = L ? "#5b6680" : "#aebbd0";
  const blockedColor = L ? "#9b3445" : "#ffc7cf";
  const openColor   = L ? "#216b5a" : "#a9e1da";

  const tbodyRowBg     = L ? "#ffffff" : "#121f3d";
  const tbodyRowEvenBg = L ? "#fafcfd" : "#152344";
  const theadBg        = "#223a7a";
  const theadColor     = "#ffffff";
  const theadBorder    = "#3db6e9";
  const cellBorderColor = L ? "#e2eaf1" : "rgba(92,134,181,0.22)";

  return (
    <div className="slt-root" style={rootStyle}>
      <style>{`
        /* Neutraliza las reglas globales de index.css dentro de esta tabla */
        .slt-root thead th {
          background: ${theadBg} !important;
          color: ${theadColor} !important;
          border-bottom: 1px solid ${theadBorder} !important;
          border-right: 1px solid ${cellBorderColor} !important;
          padding: 7px 9px !important;
          text-align: center !important;
          text-transform: none !important;
          letter-spacing: normal !important;
          font-weight: 700 !important;
          white-space: nowrap !important;
        }
        .slt-root thead th:last-child {
          border-right: 0 !important;
        }
        .slt-root tbody tr {
          background-color: ${tbodyRowBg} !important;
        }
        .slt-root tbody tr:nth-child(even) {
          background-color: ${tbodyRowEvenBg} !important;
        }
        .slt-root table {
          border-collapse: separate !important;
          border-spacing: 0 !important;
        }
        .slt-root th,
        .slt-root td {
          border: none !important;
          border-right: 1px solid ${cellBorderColor} !important;
          border-bottom: 1px solid ${cellBorderColor} !important;
          padding: 7px 9px !important;
          white-space: nowrap !important;
        }
        .slt-root td:last-child,
        .slt-root th:last-child {
          border-right: 0 !important;
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <h4 style={titleStyle}>Daily Price Snapshot</h4>
          <p style={subStyle}>
            {focusDate ? `From ${format(parseIsoLocal(focusDate) || new Date(), "dd/MM/yyyy")} (14 days)` : "Next 14 days"} · Valid until: {String(roomRateValidUntil || "Open").slice(0, 10)}
          </p>
        </div>
        {focusDate && (
          <button type="button" style={btnStyle} onClick={() => { if (typeof clearFocusDate === "function") clearFocusDate(); }}>
            {referenceDate ? "Back to Demo Date" : "Back to Today"}
          </button>
        )}
      </div>

      <div style={wrapStyle}>
        <table style={{ width: "100%", minWidth: 520, fontSize: "0.74rem" }}>
          <thead>
            <tr>
              {["Date", "Price", "Min Stay", "Max Stay", "Stop Sell"].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.iso}>
                <td style={{ textAlign: "left", color: dateColor, fontWeight: 700 }}>{row.displayDate}</td>
                <td style={{ color: row.price == null ? mutedColor : priceColor, fontWeight: row.price == null ? undefined : 800 }}>
                  {row.price == null
                    ? "-"
                    : formatStayLengthPrice(row.price, currency || baseCurrency, currencyCatalog)}
                </td>
                <td style={{ color: row.minStay == null ? mutedColor : undefined }}>
                  {row.minStay == null ? "-" : row.minStay}
                </td>
                <td style={{ color: row.maxStay == null ? mutedColor : undefined }}>
                  {row.maxStay == null ? "∞" : row.maxStay}
                </td>
                <td style={{ color: row.blocked ? blockedColor : openColor, fontWeight: 700 }}>
                  {row.blocked ? "STOP SELL" : "OPEN"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default StayLengthTable;
